import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import surveyService, { SurveyCompletionData } from '../services/survey';

const SurveyCompleted: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [loading, setLoading] = useState(true);
  const [completionData, setCompletionData] = useState<SurveyCompletionData | null>(null);

  useEffect(() => {
    if (surveyId) {
      loadCompletionData();
    }
  }, [surveyId]);

  const loadCompletionData = async () => {
    try {
      setLoading(true);
      const data = await surveyService.getCompletionData(surveyId!);
      setCompletionData(data);
    } catch (err) {
      console.error('Error loading completion data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getThankYouMessage = (language: string) => {
    const messages: Record<string, { title: string; subtitle: string; message: string }> = {
      en: {
        title: 'Thank You!',
        subtitle: 'Your feedback has been submitted successfully',
        message: 'We appreciate you taking the time to share your experience with us. Your feedback helps us improve our services and provide better care.'
      },
      es: {
        title: '¡Gracias!',
        subtitle: 'Su opinión ha sido enviada exitosamente',
        message: 'Agradecemos que se haya tomado el tiempo para compartir su experiencia con nosotros. Sus comentarios nos ayudan a mejorar nuestros servicios y brindar una mejor atención.'
      },
      fr: {
        title: 'Merci!',
        subtitle: 'Votre avis a été soumis avec succès',
        message: "Nous vous remercions d'avoir pris le temps de partager votre expérience avec nous. Vos commentaires nous aident à améliorer nos services et à fournir de meilleurs soins."
      },
      de: {
        title: 'Vielen Dank!',
        subtitle: 'Ihr Feedback wurde erfolgreich übermittelt',
        message: 'Wir danken Ihnen, dass Sie sich die Zeit genommen haben, Ihre Erfahrungen mit uns zu teilen. Ihr Feedback hilft uns, unsere Dienstleistungen zu verbessern und eine bessere Betreuung zu bieten.'
      },
      it: {
        title: 'Grazie!',
        subtitle: 'Il tuo feedback è stato inviato con successo',
        message: 'Ti ringraziamo per aver dedicato del tempo a condividere la tua esperienza con noi. Il tuo feedback ci aiuta a migliorare i nostri servizi e fornire cure migliori.'
      },
      pt: {
        title: 'Obrigado!',
        subtitle: 'Seu feedback foi enviado com sucesso',
        message: 'Agradecemos por dedicar seu tempo para compartilhar sua experiência conosco. Seu feedback nos ajuda a melhorar nossos serviços e fornecer um melhor atendimento.'
      },
      nl: {
        title: 'Bedankt!',
        subtitle: 'Uw feedback is succesvol verzonden',
        message: 'We waarderen het dat u de tijd heeft genomen om uw ervaring met ons te delen. Uw feedback helpt ons onze diensten te verbeteren en betere zorg te bieden.'
      }
    };

    return messages[language] || messages.en;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        </Paper>
      </Container>
    );
  }

  const messages = getThankYouMessage(completionData?.language || 'en');

  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
        </Box>
        
        <Typography variant="h3" gutterBottom>
          {messages.title}
        </Typography>
        
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {messages.subtitle}
        </Typography>
        
        <Typography variant="body1" sx={{ mt: 3, mb: 4 }}>
          {messages.message}
        </Typography>
        
        {completionData && (
          <Paper sx={{ p: 2, bgcolor: 'grey.100', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Clinic:</strong> {completionData.clinicName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Submitted:</strong>{' '}
              {new Date(completionData.completedAt).toLocaleDateString()} at{' '}
              {new Date(completionData.completedAt).toLocaleTimeString()}
            </Typography>
          </Paper>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          You may now close this window
        </Typography>
      </Paper>
    </Container>
  );
};

export default SurveyCompleted;